/**
 * Hook for card index operations using preloaded MinimalCard data
 *
 * Loads minimal card index from CDN with IndexedDB caching.
 * Provides optimized lookup functions for import matching and card discovery.
 *
 * NOTE: This index contains MinimalCard data only (not full ScryfallCard).
 * For full card data, use useCardLookup hook or fullCardCacheAtom.
 */
import type { MatchedImportCard } from '@/features/import-export/types';
import { useImportCardMatcher } from '@/hooks/useImportCardMatcher';
import {
  cardIndexAtom,
  cardIndexByIdAtom,
  cardIndexErrorAtom,
  cardIndexLoadingAtom,
  cardIndexProgressAtom,
  cardIndexStatsAtom,
  type MinimalCard,
} from '@/services/cardDatabase';
import { firebaseConfig } from '@/services/firebase';
import { getCachedCards, getCacheMetadata, isIndexedDBSupported, setCachedCards, setCacheMetadata } from '@/services/indexedDbCache';
import type { ImportCard } from '@/types/schemas';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

// CDN URL for card index (Firebase Storage public URL)
const CARD_INDEX_URL = `https://storage.googleapis.com/${firebaseConfig.storageBucket}/public/cards-index.json`;

/**
 * Fetch the current index version from Firestore
 */
async function fetchIndexVersion(): Promise<{ version: number; cardCount: number } | null> {
  try {
    const db = getFirestore();
    const docRef = doc(db, 'syncMetadata', 'cardIndex');
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    return {
      version: data.version ?? 0,
      cardCount: data.card_count ?? 0,
    };
  } catch (error) {
    console.warn('Failed to fetch index version:', error);
    return null;
  }
}

/**
 * Fetch card index from CDN
 */
async function fetchCardIndex(): Promise<MinimalCard[]> {
  const response = await fetch(CARD_INDEX_URL);

  if (!response.ok) {
    throw new Error(`Failed to fetch card index: ${response.status}`);
  }

  return response.json();
}

/**
 * Hook to load the card index on app startup
 * Uses CDN + IndexedDB caching for fast loading
 */
export function useCardIndexLoader() {
  const [loading, setLoading] = useAtom(cardIndexLoadingAtom);
  const setCards = useSetAtom(cardIndexAtom);
  const setError = useSetAtom(cardIndexErrorAtom);
  const setProgress = useSetAtom(cardIndexProgressAtom);

  useEffect(() => {
    let cancelled = false;

    async function loadCards() {
      try {
        setLoading(true);
        setError(null);

        // Check if IndexedDB is supported
        const hasIndexedDB = isIndexedDBSupported();

        // 1. Check cached version
        let cachedCards: MinimalCard[] = [];
        let cacheVersion = 0;

        if (hasIndexedDB) {
          setProgress('Checking local cache...');
          const metadata = await getCacheMetadata();
          if (metadata) {
            cacheVersion = metadata.version;
          }

          // Try to load from cache first (fast path)
          if (cacheVersion > 0) {
            setProgress('Loading from cache...');
            cachedCards = await getCachedCards<MinimalCard>();

            if (cachedCards.length > 0 && !cancelled) {
              // Use cached data immediately while checking for updates
              setCards(cachedCards);
              setProgress('Checking for updates...');
            }
          }
        }

        // 2. Check server version
        const serverVersion = await fetchIndexVersion();

        // If no server version available, use cache or fail
        if (!serverVersion) {
          if (cachedCards.length > 0) {
            setProgress('Using cached data (offline)');
            setLoading(false);
            return;
          }
          throw new Error('Card index not available. Please try again later.');
        }

        // 3. If cache is current, we're done
        if (cacheVersion === serverVersion.version && cachedCards.length > 0) {
          setProgress('Cache is up to date');
          setLoading(false);
          return;
        }

        // 4. Fetch fresh data from CDN
        setProgress(`Downloading ${serverVersion.cardCount.toLocaleString()} cards...`);
        const freshCards = await fetchCardIndex();

        if (cancelled) return;

        // 5. Update state
        setCards(freshCards);

        // 6. Save to IndexedDB cache
        if (hasIndexedDB) {
          setProgress('Saving to cache...');
          await setCachedCards(freshCards);
          await setCacheMetadata({
            version: serverVersion.version,
            cardCount: freshCards.length,
            updatedAt: new Date().toISOString(),
          });
        }

        setProgress('Ready');
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load card index';
          setError(message);
          setLoading(false);
        }
      }
    }

    loadCards();

    return () => {
      cancelled = true;
    };
  }, [setCards, setError, setLoading, setProgress]);

  return { loading };
}

/**
 * Hook for accessing the card index with optimized lookups
 *
 * Use this for:
 * - Card discovery/search when adding cards to decks
 * - Import matching (name/set/collector_number lookups)
 * - Getting card image URIs
 *
 * For full card data (prices, detailed legalities), use useCardLookup instead.
 */
export default function useCardIndex() {
  const stats = useAtomValue(cardIndexStatsAtom);
  const allCards = useAtomValue(cardIndexAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);
  const loading = useAtomValue(cardIndexLoadingAtom);
  const error = useAtomValue(cardIndexErrorAtom);

  // Delegate lookup + matching to shared hook
  const matcher = useImportCardMatcher();

  // O(n) lookup for all printings of a card name
  const getAllPrintingsByName = useCallback(
    (name: string): MinimalCard[] => {
      const nameLower = name.toLowerCase();
      return allCards.filter((card) => card.name_lower === nameLower).sort((a, b) => a.set.localeCompare(b.set));
    },
    [allCards],
  );

  // O(n) lookup for the first card matching an oracle ID
  const getCardByOracleId = useCallback(
    (oracleId: string): MinimalCard | null => {
      if (!oracleId) return null;
      const matches = allCards.filter((card) => card.oracle_id === oracleId);
      if (matches.length === 0) return null;
      return matches.sort((a, b) => b.set.localeCompare(a.set))[0] ?? null;
    },
    [allCards],
  );

  // Batch lookup by IDs
  const getCardsByIds = useCallback(
    (ids: string[]): Map<string, MinimalCard> => {
      const result = new Map<string, MinimalCard>();
      for (const id of ids) {
        const card = cardsById.get(id);
        if (card) {
          result.set(id, card);
        }
      }
      return result;
    },
    [cardsById],
  );

  // Backward-compatible matchImportCards using shared matcher
  const matchImportCards = useCallback(
    (cards: ImportCard[]): MatchedImportCard[] => {
      return cards.map((card) => {
        const result = matcher.matchCard(card);
        return {
          ...card,
          matched: result.matchedCard,
          matchType: result.matchType,
          error: result.matchError,
        };
      });
    },
    [matcher],
  );

  return {
    loading,
    error,
    stats,
    getCardById: matcher.getCardById,
    getCardByOracleId,
    getCardBySetNumber: matcher.getCardBySetNumber,
    getCardByName: matcher.getCardByName,
    getAllPrintingsByName,
    getCardsByIds,
    searchCardsByPrefix: matcher.searchCardsByPrefix,
    matchImportCards,
  };
}
